import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IonContent, IonPage } from '@ionic/react';
import { useParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { formatMoney } from '@tcrd/shared';
import { BrandLogo, Button, Card, ErrorState, Notice, Spinner } from '@tcrd/ui';
import { api, errorMessage } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';

interface PublicQuote {
  status: string;
  amount: string;
  currency: string;
  tolls: string;
  includedServices: string[];
  conditions: string | null;
  expiresAt: string;
  service: string;
  origin: string;
  destination: string;
  scheduledAt: string | null;
  driver: string;
  customerFirstName: string;
}

/** Cotización abierta desde un enlace firmado (WhatsApp), sin iniciar sesión. */
export function PublicQuotePage() {
  const { token } = useParams<{ token: string }>();
  const q = useQuery({ queryKey: ['public-quote', token], queryFn: () => api<PublicQuote>(`/public/quotes/${token}`), retry: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const respond = async (accept: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await api(`/public/quotes/${token}/${accept ? 'accept' : 'reject'}`, { method: 'POST' });
      await q.refetch();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const d = q.data;
  return (
    <IonPage>
      <IonContent>
        <div className="min-h-full flex flex-col items-center px-4 py-8 gap-6">
          <BrandLogo className="h-16" />
          {q.isLoading && <Spinner />}
          {q.error && <ErrorState message={errorMessage(q.error)} />}
          {d && (
            <Card className="w-full max-w-md p-6 flex flex-col gap-4">
              <p className="text-sm text-[var(--text-muted)]">Hola {d.customerFirstName}, esta es tu cotización</p>
              <p className="text-4xl font-extrabold tracking-tight">{formatMoney(d.amount, d.currency)}</p>
              <div className="text-sm flex flex-col gap-1">
                <p><span className="text-[var(--text-muted)]">Servicio:</span> {d.service}</p>
                <p><span className="text-[var(--text-muted)]">Desde:</span> {d.origin}</p>
                <p><span className="text-[var(--text-muted)]">Hasta:</span> {d.destination}</p>
                {d.scheduledAt && <p><span className="text-[var(--text-muted)]">Fecha:</span> {fmtDateTime(d.scheduledAt)}</p>}
                <p><span className="text-[var(--text-muted)]">Conductor:</span> {d.driver}</p>
                {Number(d.tolls) > 0 && <p>Incluye peajes por {formatMoney(d.tolls)}</p>}
                {d.conditions && <p className="mt-1">{d.conditions}</p>}
              </div>
              {d.status === 'PENDING' && (
                <>
                  <p className="text-xs text-[var(--text-muted)]">Válida hasta {fmtDateTime(d.expiresAt)}. El pago se realiza directamente al conductor.</p>
                  {error && <Notice tone="danger">{error}</Notice>}
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="secondary" icon={<X className="size-5" />} disabled={busy} onClick={() => respond(false)}>Rechazar</Button>
                    <Button variant="accent" icon={<Check className="size-5" />} loading={busy} onClick={() => respond(true)}>Aceptar</Button>
                  </div>
                </>
              )}
              {d.status === 'ACCEPTED' && <Notice>✅ Reserva confirmada. Te avisaremos por WhatsApp cuando el conductor vaya en camino.</Notice>}
              {d.status === 'REJECTED' && <Notice>Rechazaste esta cotización. El conductor puede enviarte otra.</Notice>}
              {['EXPIRED', 'SUPERSEDED', 'WITHDRAWN'].includes(d.status) && <Notice tone="warning">Esta cotización ya no está vigente.</Notice>}
            </Card>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
